<template>
	<div class="ChecklistFeaturedMedia">
		<div v-if="featuredMediaId" class="ChecklistFeaturedMedia-media">
			<button type="button" @click="openLightbox($event, featuredMediaId)" :aria-label="featuredMediaAsset && featuredMediaAsset.taxon && featuredMediaAsset.taxon.comName ? featuredMediaAsset.taxon.comName : title(featuredMediaAsset)">
				<img 
					:alt="featuredMediaAsset && featuredMediaAsset.taxon && featuredMediaAsset.taxon.comName ? featuredMediaAsset.taxon.comName : title(featuredMediaAsset)"
					:src="`${mediaDownloadUrl}${featuredMediaId}/640`" 
					:srcset="`${mediaDownloadUrl}${featuredMediaId}/320 320w,
						${mediaDownloadUrl}${featuredMediaId}/480 480w,
						${mediaDownloadUrl}${featuredMediaId}/640 640w,
						${mediaDownloadUrl}${featuredMediaId}/900 900w,
						${mediaDownloadUrl}${featuredMediaId}/1200 1200w`"
					data-sizes="(min-width: 768px) 480px, 100vw"
				/>
			</button>
			<div v-if="featuredMediaAsset" class="ChecklistFeaturedMedia-media-data">
				<CloSpecies v-if="featuredMediaAsset.taxon && featuredMediaAsset.taxon.comName" :commonName="featuredMediaAsset.taxon.comName" :sciName="featuredMediaAsset.taxon.sciName" :sppPref="sppPref" class="u-inline-sm" />
				<span class="bold u-inline-sm" v-else>{{ title(featuredMediaAsset) }}</span>
				<span>&copy;&nbsp;{{ featuredMediaAsset.userDisplayName }}</span>
			</div>
		</div>
		<button v-if="isEditable && hasPhotos" ref="button" type="button" class="Button Button--tiny Button--hollow u-margin-none" style="padding: .25rem .4rem;" @click="openDialog">
			<span v-if="featuredMediaId">{{ $t('checklist.changeFeaturedImage') }}</span>
			<span v-else>{{ $t('checklist.chooseFeaturedImage') }}</span>
		</button>
		<CloDialog v-if="showDialog" @close="closeDialog">
			<template #header>
				<CloHeading level="1" size="3" class="u-stack-md">{{ $t('checklist.featuredImageTitle') }}</CloHeading>
			</template>
			<template #body>
					<ul class="ChecklistFeaturedMedia-list">
						<template v-for="media in checklistMedia">
							<li v-if="media.assetFormatCode === 'photo'" :key="media.assetId" class="ChecklistFeaturedMedia-card">
								<div v-if="media.assetId === featuredMediaId" class="ChecklistFeaturedMedia-card-featured">
									{{ $t('checklist.featured') }}
								</div>
								<button type="button" @click="selectMedia(media, 'checklist')" :aria-label="media.assetId === featuredMediaId ? $t('checklist.unsetFeaturedImage') : $t('checklist.setFeaturedImage')">
									<img
										:src="`${mediaDownloadUrl}${media.assetId}/640`"
										:alt="featuredMediaAsset && featuredMediaAsset.taxon && featuredMediaAsset.taxon.comName ? featuredMediaAsset.taxon.comName : title(featuredMediaAsset)"
									/>
								</button>
								<div class="ChecklistFeaturedMedia-card-data">
									<div class="bold">{{ title(media) }}</div>
									<div>&copy;&nbsp;{{ media.userDisplayName }}</div>
								</div>
							</li>
						</template>
						<template v-for="media in obsMedia">
							<li v-if="media.assetFormatCode === 'photo'" :key="media.assetId" class="ChecklistFeaturedMedia-card">
								<div v-if="media.assetId === featuredMediaId" class="ChecklistFeaturedMedia-card-featured">
									{{ $t('checklist.featured') }}
								</div>
								<button type="button" @click="selectMedia(media, 'obs')" :aria-label="media.assetId === featuredMediaId ? $t('checklist.unsetFeaturedImage') : $t('checklist.setFeaturedImage')">
									<img
										:src="`${mediaDownloadUrl}${media.assetId}/640`"
										:alt="featuredMediaAsset && featuredMediaAsset.taxon && featuredMediaAsset.taxon.comName ? featuredMediaAsset.taxon.comName : title(featuredMediaAsset)"
									/>
								</button>
								<div class="ChecklistFeaturedMedia-card-data">
									<CloSpecies :commonName="media.taxon.comName" :sciName="media.taxon.sciName" :sppPref="sppPref" />
									<div>&copy;&nbsp;{{ media.userDisplayName }}</div>
								</div>
							</li>
						</template>
					</ul>
			</template>
			<template #footer>
				<div class="center">
					<button type="button" class="Button" @click="closeDialog">{{ $t('generic.done') }}</button>
				</div>
			</template>
		</CloDialog>
		<CloLightbox
			v-if="lightboxOpen"
			reverse
			externalLightbox
			dataFormat="search2.0"
			mediaVersion="2.0"
			:list="[featuredMediaAsset]"
			:mediaDownloadUrl="mediaDownloadUrl"
			:ebirdWebUrl="ebirdWebUrl"
			:mediaSpecimenUrl="mediaSpecimenUrl"
			ratingEnabled
			:getRatingsUrl="getRatingsUrl"
			:saveRatingUrl="saveRatingUrl"
			:ratingCsrf="ratingCsrf"
			@close="closeLightbox"
		></CloLightbox>
	</div>
</template>

<script>
import axios from 'axios';
import CloDialog from '@/base/CloDialog/CloDialog.vue';
import CloSpecies from '@/base/CloSpecies/CloSpecies.vue';
import CloLightbox from '@/media/CloLightbox/CloLightbox.vue';

export default {
    name: 'ChecklistFeaturedMedia',
    components: { CloDialog, CloSpecies, CloLightbox },
    data() {
        return {
			showDialog: false,
			checklistMedia: window.checklistMediaJson,
			obsMedia: window.mediaJson,
			csrf: window._csrf,
			featuredMediaId: this.featuredMediaProp[0] ? this.featuredMediaProp[0] : null,
			featuredMedia: this.featuredMediaProp,
			categories: [
				'environmental',
				'experience',
				'other_animals'
			],
			lightboxOpen: false,
			hasPhotos: false,
        };
    },
    props: {
        mediaDownloadUrl: {
            type: String,
            required: true,
        },
        baseUrl: {
            type: String,
            required: true,
        },
		isEditable: {
			type: Boolean,
			default: false,
		},
		featuredMediaProp: {
			type: Array,
			default: () => [],
		},
		subId: {
			type: String,
			required: true,
		},
		sppPref: {
			type: String,
			default: 'both',
		},
		checklistMediaDisplayOrder: {
			type: Object,
			default: () => {},
		},
        ebirdWebUrl: {
            type: String,
            required: true,
        },
        mediaSpecimenUrl: {
            type: String,
            required: true,
        },
		getRatingsUrl: {
			type: String,
			required: true,
		},
		saveRatingUrl: {
			type: String,
			required: true,
		},
		ratingCsrf: {
			type: String,
			required: true,
		},
    },
    created() {
		if (!Array.isArray(this.checklistMedia)) {
			// if checklistMedia is empty, it's an empty object but we need an empty array
			this.checklistMedia = []
		}
		if (!Array.isArray(this.obsMedia)) {
			// if obsMedia is empty, it's an empty object but we need an empty array
			this.obsMedia = []
		}
		this.hasPhotos = this.checklistMedia.some(media => media.assetFormatCode === 'photo') || this.obsMedia.some(media => media.assetFormatCode === 'photo');
		// make assetIds numbers not strings
		this.obsMedia = this.obsMedia.map(x => {
			x.assetId = Number(x.assetId);
			return x;
		});
		this.checklistMedia = this.checklistMedia.map(x => {
			x.assetId = Number(x.assetId);
			return x;
		});
		// featuredMediaProp is an array of strings, we need to convert it to an array of numbers
		this.featuredMedia = this.featuredMediaProp.map(x => Number(x));
		
		// sort media by participant with checklist owner's media first
		const participants = document.querySelectorAll('[data-participant-userid]')
		const participantIds = Array.prototype.map.call(participants, (participant) => {
			return participant.getAttribute('data-participant-userid');
		});

		// set display order, sort by category, user, display order, then asset id
		this.checklistMedia = this.checklistMedia.map((a) => {
			a.displayOrder = this.checklistMediaDisplayOrder[a.assetId] || 0;
			return a;
		}).sort((a, b) => {
			if (participantIds.indexOf(a.userId) < participantIds.indexOf(b.userId)) {
				return - 1
			}
			if (participantIds.indexOf(a.userId) > participantIds.indexOf(b.userId)) {
				return 1
			}
			const aCategory = this.categories.find((category) => 'assetTagCodes' in a && a.assetTagCodes.includes(category));
			const bCategory = this.categories.find((category) => 'assetTagCodes' in b && b.assetTagCodes.includes(category));
			if (aCategory && bCategory) {
				if (this.categories.indexOf(aCategory) < this.categories.indexOf(bCategory)) {
					return - 1
				}
				if (this.categories.indexOf(aCategory) > this.categories.indexOf(bCategory)) {
					return 1
				}
			}
			if (a.displayOrder < b.displayOrder) {
				return -1;
			}
			if (a.displayOrder > b.displayOrder) {
				return 1;
			}
			if (a.assetId < b.assetId) {
				return -1;
			}
			if (a.assetId > b.assetId) {
				return 1;
			}
			return 0;
			// set all the missing data
		}).map((asset) => {
			asset.mediaType = asset.assetFormatCode;
			asset.tags = asset.assetTagCodes;
			//asset.taxonomy = asset.taxon ? asset.taxon : { speciesCode: null };
			// get user information from html on page
			const userElement = document.querySelector('[data-participant-userid="' + asset.userId + '"]');
			const userDisplayName = userElement.querySelector('[data-participant-userdisplayname]').textContent;
			asset.userDisplayName = userDisplayName;
			return asset;
		});

		const obsMediaElements = document.querySelectorAll('[data-media-id]');
		const obsMediaIds = Array.prototype.map.call(obsMediaElements, (media) => {
			return media.getAttribute('data-media-id');
		});
		let obsMediaSpeciesCodes = Array.prototype.map.call(obsMediaElements, (media) => {
			return media.getAttribute('data-media-speciescode');
		});
		obsMediaSpeciesCodes = [...new Set(obsMediaSpeciesCodes)];
		// sort obsMedia by order speciesCode, then participant, then order on page
		// this is the order on the page
		this.obsMedia = this.obsMedia.map((asset) => {
			const el = document.querySelector('[data-media-id="' + asset.assetId + '"]');
			if (el) {
				asset.taxon = {
					comName: el.getAttribute('data-media-commonname'),
					sciName: el.getAttribute('data-media-sciname'),
					speciesCode: el.getAttribute('data-media-speciescode'),
					reportAs: el.getAttribute('data-media-reportas'),
					category: el.getAttribute('data-media-taxoncategory'),
				};
			}
			return asset;
		}).sort((a, b) => {
			if (obsMediaSpeciesCodes.indexOf(a.taxon.speciesCode) < obsMediaSpeciesCodes.indexOf(b.taxon.speciesCode)) {
				return - 1
			}
			if (obsMediaSpeciesCodes.indexOf(a.taxon.speciesCode) > obsMediaSpeciesCodes.indexOf(b.taxon.speciesCode)) {
				return 1
			}
			if (participantIds.indexOf(a.userId) < participantIds.indexOf(b.userId)) {
				return - 1
			}
			if (participantIds.indexOf(a.userId) > participantIds.indexOf(b.userId)) {
				return 1
			}
			if (obsMediaIds.indexOf(a.assetId.toString()) < obsMediaIds.indexOf(b.assetId.toString())) {
				return - 1
			}
			if (obsMediaIds.indexOf(a.assetId.toString()) > obsMediaIds.indexOf(b.assetId.toString())) {
				return 1
			}
			return 0;
		})
		
	},
	computed: {
		featuredMediaAsset() {
			let media = {
				assetId: null,
			};
			// make assetIds numbers not strings
			this.obsMedia = this.obsMedia.map(x => {
				x.assetId = Number(x.assetId);
				return x;
			});
			if (this.featuredMediaId) {
				media = this.checklistMedia.find(media => media.assetId === this.featuredMediaId);
			}
			if (!media) {
				media = this.obsMedia.find(media => media.assetId === this.featuredMediaId);
			}
			return media;
		},
	},
	methods: {
		openLightbox(e, assetId) {
			this.lightboxOpen = true;
			this.opener = e.target;
		},
		closeLightbox() {
			this.lightboxOpen = false;
			if (this.opener) {
				this.opener.focus();
			}
			this.opener = null;
		},
		openDialog() {
			this.showDialog = true;
		},
		closeDialog() {
			this.showDialog = false;
			this.$refs.button.focus();
		},
		async selectMedia(asset, type) {
			if (this.featuredMedia.length) {
				// removing featured media
				if (this.featuredMediaId == asset.assetId) {
					await this.saveChange(asset, type, 'delete');
				} else {
					// if there is already a featured media, we need to delete it first
					let currentFeaturedMedia = this.checklistMedia.find(media => media.assetId === this.featuredMediaId);
					if (currentFeaturedMedia) {
						await this.saveChange(currentFeaturedMedia, 'checklist', 'delete');
						await this.saveChange(asset, type, 'add');
					}
					else {
						let currentFeaturedMedia = this.obsMedia.find(media => media.assetId === this.featuredMediaId);
						if (currentFeaturedMedia) {
							await this.saveChange(currentFeaturedMedia, 'obs', 'delete');
							await this.saveChange(asset, type, 'add');
						}
					}
				}
			} else {
				// adding featured media
				await this.saveChange(asset, type, 'add');
			}
		},
		async saveChange(asset, type, action) {
			let payload = {};
			if (type === 'obs') {
				// get obsId from html on page
				const obsId = document.querySelector('[data-media-id="' + asset.assetId + '"]').getAttribute('data-media-obsid');
				payload = {
					obsId: obsId,
					obsAssetId: asset.assetId,
				};
			} else {
				payload = {
					subAssetId: asset.assetId.toString()
				} 
			}
			//console.log(action, type, payload);
			const headers = {
				'X-CSRF-TOKEN': this.csrf
			};
			const url = `${this.baseUrl}media-assets/${action}-featured-media/${this.subId}`;
			await axios
				.post(url, payload, {
					headers: headers,
					withCredentials: true,
				})
			.then((response) => {
				if (response.status === 200) {
					//console.log('success', action)
					if (action === 'add') {
						this.featuredMediaId = asset.assetId;
						this.featuredMedia = [asset.assetId];
					} else {
						this.featuredMediaId = null;
						this.featuredMedia = [];
					}
				}
			}).catch((error) => {
				console.error('Error saving featured media:', error);
			});
		},
		title(asset) {
			let title = [];
			if (asset.tags) {
				if (asset.tags.includes('environmental')) {
					if (asset.mediaType === 'photo') {
						title.push(this.$t('mediaTag.tags.habitatPhoto'));
					}
					if (asset.mediaType === 'audio') {
						title.push(this.$t('mediaTag.tags.soundscape'));
					}
					if (asset.mediaType === 'video') {
						title.push(this.$t('mediaTag.tags.environmentalVideo'));
					}
				}
				else if (asset.tags.includes('experience')) {
					title.push(this.$t('mediaTag.tags.experience'));
				}
				else if (asset.tags.includes('other_animals')) {
					if (asset.taxon && asset.taxon.comName) {
						title.push(asset.taxon.comName);
					}
					else {
						title.push(this.$t('mediaTag.tags.other_animals'));
					}
				}
			}
			if (asset.caption) {
				title.push(asset.caption);
			}
			return title.join(': ');
		}
    },
}
</script>

<style lang="scss">
.ChecklistFeaturedMedia {
	@include stack-md;
	.ChecklistFeaturedMedia-media {
		@include stack-xs;
		max-width: 480px;
		.ChecklistFeaturedMedia-media-data {
			@include text(1);
			@include inset-squish-sm;
			background-color: $color-neutral-1;
		}
	}
	.ChecklistFeaturedMedia-list {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr));
		grid-gap: $space-sm;
		list-style: none;
		padding: 0;
		@include stack-md;
		.ChecklistFeaturedMedia-card {
			@include text(1);
			display: flex;
			flex-direction: column;
			button {
				&:hover img {
					opacity: 0.8;
				}
			}
			.ChecklistFeaturedMedia-card-data {
				background-color: $color-neutral-1;
				@include inset-squish-sm;
			}
			.ChecklistFeaturedMedia-card-featured {
				background-color: $brand-color-forest-green-10;
				@include text(2);
				font-weight: bold;
				color: $color-white;
				@include inset-squish-sm;
				text-align: center;
			}
		}
	}
	.Dialog {
		.Dialog-container {
			max-width: 1200px !important;
		}
	}
	.center {
		display: flex;
		justify-content: center;
		margin-block-start: $space-sm;
		width: 100%;
	}
	.bold {
		font-weight: bold;
	}
}
</style>